export default {
  electrical: {
    writers:[
      {
        name: 'Briana Cantos',
        description: false,
        photo: false
      }
    ],
    appliedSkills: [
      "Components and symbols",
      "Circuit diagrams",
      "LEDs and diodes",
      "Transistors",
      "Semiconductors",
      "Motor types",
      "Multimeters",
      "Soldering",
      "Voltage division",
      "Sensors",
      "Motor controllers and H-bridges",
      "Voltage regulators",
      "Microcontrollers ",
      "Event driven interrupts"
    ], 
    conceptualSkills: [
      "Electromagnetics",
      "Circuit Theory",
      "Voltage, current, resistance and power",
      "Ohm’s Law ",
      "Kirchhoff's voltage and current laws",
      "Series and parallel circuits",
      "Signals",
      "Radio frequency",
      "Filtering"
    ],
    description: [
      "Electrical engineers use relatively simple equations to understand the flow of electrons through resistors, capacitors, and inductors. In this course, you will learn how to use such devices, including connecting them, modeling them, and directing life-giving electricity to them in *just* the right way to produce the effect desired. This track will focus on embedded electronics, fundamental to robotics, and even introduce coding- necessary for electrical engineers to understand in order to control energy and power in mechatronic systems.",
      "Electricity powers our modern lives and the electrical engineer is in charge (no pun intended) of distributing, transforming, storing, and using that power. Electrical engineers can look at a circuit and tell you the number of electrons flowing between two points. As you will learn, flowing electrons produce near magical effects when handled just right, like producing heat to keep components alive in deep space, changing radio signals to transmit data, or even generating powerful magnetic fields that help produce huge torques in modern motors. Our workshops focus on creating systems which can sense their environment and then act based on that input. Of the team, you will own the connection of the mechanical and computing components, and that will require understanding of all three disciplines.",
      "As a new electrical engineer, you will learn to use simple circuits, motors, and software in increasingly complex combinations to produce real, physical devices that can sense and interact with the world. You will understand energy flow in the universe (in the literal sense) and learn how to control the flow of electricity through devices, producing power for each component. Through electrical engineering, you also  learn how software can change the functionality of devices and systems, gaining the ability to re-purpose an existing set of hardware and elements to behave in a whole new way."
    ],
    disciplineLead: [
      {
        name: 'Victor Ardulov',
        description: false,
        photo: false,
      }
    ],
    title: "Electrical Engineering Syllabus"
  },
  essential: {
    appliedSkills: [
      "Motors",
      "Sensors for feedback systems",
      "Datasheets",
      "Optical devices",
      "Networking",
      "Microcontrollers",
      "Embedded systems"
    ],
    writers: [
      {
        name: 'Taylor Halsey',
        description: 'Mind Maker, Microbiology and Biotechnology Masters Student',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/TaylorHalsey200x200.jpg?raw=true'
      },
      {
        name: 'Faviola Dadis',
        description: 'Neuroscientist, researcher, author, doctoral candidate',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/FaviolaDadis200x200.jpg?raw=true'
      },
    ],
    conceptualSkills: [
      "STEM Ethics & Philosophy",
      "The scientific method",
      "Physics of sound",
      "Optics ",
      "Anatomy of the senses",
      "Sensory processing of the brain",
      "Signal processing",
      "Algebra, trigonometry and vector math"
    ],
    description: [
      "So you want to be an engineer. The hardest part of becoming an engineer is choosing *which* engineer you want to become. Aerospace, Biomedical, Mechanical, Petroleum, Electrical, Controls, Software, Robotics, Structural, Civil, and many more, are all valid words to put in front of the word 'Engineer'. Across the wide variety of specialties, there are a few common threads: working by processes, transferring knowledge, creative problem solving, clear writing, and yes, some math. Our engineering essentials curriculum is designed to present an overview of basic math and core engineering concepts needed to understand what an engineer does, and showcase interrelations between and introduce different STEM fields. By following this track, one is exposed to the introduction needed to dive into any of our three core tracks, electrical, mechanical or software, or to feel confident trying a totally new STEM field entirely.",
      "One of the most exciting developments that crosscuts many engineering disciplines is the coming proliferation of robotic, autonomous systems. An autonomous system is one that mimics life, but in a controlled way. Humans, animals, plants and robots all respond to their environments expressing motion, sound and a variety or types of responses. In just a few weeks you will use the skills you've learned in our hands-on curriculum to develop modules which are designed to emulate real human sensor response systems, and pick up a lot of useful experience with mechanical, electrical, and software systems along the way.",
      "Electricity, mechanics and software is everywhere around us and the parallels between automation in living and non-living systems grow as technology and our knowledge of science expands. This track will allow students to better understand how their own bodies function and understand similarities between ourselves and other systems. In addition to a new appreciation for the complexities of living systems, you'll learn to become more detail-oriented, rigorous, and analytical in your approach to problems. Engineering is, after all, creative problem solving."
    ],
    disciplineLead: [
      {
        name: 'Adriana Gavrilciuc',
        description: 'Multilingual, Professional Biotech Devices',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/AdrianaGavrilciuc200x200.jpg?raw=true'
      }
    ],
    title: "Engineering Essentials Syllabus"
  },
  mechanical: {
    writers: [
      {
        name: 'Tony Marshallsay',
        description: 'Professional Mechanical Engineer',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/TonyMarshallsay200x200.jpg?raw=true'
      },
      {
        name: 'Phoebe Rhodes-Wickett',
        description: 'Mechanical Engineer at JPL',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/PhoebeRhodes-Wickett200x200.jpg?raw=true'
      },
      {
        name: 'Maryam Tebyani',
        description: 'Undergrad Robotics Engineer',
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/MaryamTebyani200x200.jpg?raw=true'
      },
    ],
    appliedSkills: [
      "CAD (computer aided design)",
      "Tolerances",
      "Manufacturing Processes",
      "Design process",
      "Prototyping",
      "Springs",
      "Oscillations",
      "Motor types",
      "Gear ratios",
      "Linkages",
      "Sensors"
    ],
    conceptualSkills: [
      "Mechanics",
      "Forces",
      "Moments and torque",
      "Stress and strain",
      "Kinematics",
      "Position, velocity and acceleration",
      "Parabolic motion and gravity",
      "Rotational motion",
      "Energy equations",
      "Conservation of energy"
    ],
    description: [
      "Using 3D modeling software and having an intimate knowledge of physics, the mechanical engineer can design enormously complex devices made of components that might fit together to within the width of a human hair, if necessary. You will follow your design through the manufacturing process, watching it take shape before your eyes and understanding how small errors in producing parts can affect the end product. In this track you will learn about best design practices and consider tolerances and redundancies into devices from the start. In the later stages, mechanical engineers work together with electrical and software engineers to bring their creations to life.",
      "The mechanical engineer focuses on designing and building physical systems which can interact with the world through the use of sensors, signals, lights, sound and motion. You are producing tangible results, as much a sculptor as an engineer as you design components using analysis and your imagination. Through our workshops, you will practice design and manufacturing techniques, including laser cutting and 3D printing designs, and combining components with motors and electronics to build simple but illustrative autonomous systems.",
      "As with any engineering, mechanical engineering requires an attention to detail and foresight of design that is useful in any area of life. However, more than other disciplines, a mechanical engineer develops a spatial awareness and ability to visualize designs and the way that things work in their early stages. You will learn to plan far in advance to anticipate the needs of the final product. Also, you will learn to optimize designs to consider stability, weight, cost, and lifetime all at once, balancing the intricate trade offs in a way unique to your chosen field. And this involves a *lot* of cool mechanical models and intricate drawings."
    ],
    disciplineLead: [
      {
        name: 'Jeff Carlson',
        description: false,
        photo: false
      }
    ],
    title: "Mechanical Engineering Syllabus"
  },
  software: {
    writers: [
      {
        name: 'Andreas Kellas',
        description: false,
        photo: false
      },
      {
        name: 'Brendan Crosser-McGay',
        description: false,
        photo: false
      }
    ],
    appliedSkills: [  
      "Headers, libraries and using existing code",
      "Debugging",
      "Signals and conditioning",
      "Controlling devices",
      "Microcontrollers",
      "Sensor response",
      "Event driven interrupts",
      "Motor controllers and H-bridges",
      "Networking"
    ],
    conceptualSkills: [
      "Data types",
      "Variables",
      "Arrays",
      "Code flow and logic tracing ",
      "Conditional & loop structures",
      "Block programming",
      "Basic CPU architecture",
      "Functions",
      "Data Structures and embedded storage"
    ],
    description: [
      "That vast majority of software runs on tiny computers that do not have screens. This software controls stop lights, the brakes on your car, your thermostat, and so on. Our coursework crosses between interacting with devices and designing programs that are more recognizable to casual computer users. We focus on the interaction of multiple computers: those controlling the motors and lights of exhibits, reading data from sensors and transmitting it, or doing heavy processing on data to emulate living brains. We will also teach you to use tools to generate beautiful displays for your data.",
      "Software engineers take a problem, decompose it into logical relationships, design a solution to that problem, and define a step-by-step method for recognizing the problem then solving the problem using the solution they designed. In our workshops, we will design autonomous systems, which can sense things about the world, decide what to do based on the sensory data, and act by interacting and communicating with the world. We will focus on Arduino microcontrollers, and along the way you will learn about different languages, each of which have different (but similar) steps you can use to solve a problem. ",
      "Becoming a software engineer is a transition into thinking like a computer. You will start to become naturally good at approaching technical problems and decomposing them into steps which can help drive a solution. More tangibly, you’ll learn to write programs in C (a very common and respected language that controls spacecrafts, ATMs, cell phones and more) and Python (one of the most popular languages for web, hobby, and research programming). This will require an understanding of many of the core issues in software engineering, including networking, algorithm analysis, timing and concurrency and more."
    ],
    disciplineLead: [
      {
        name: 'Josh Vander Hook',
        description: "Research Technologist at NASA’s Jet Propulsion Lab",
        photo: 'https://github.com/MindMakersProject/website-BE/blob/master/photos/team/JoshVanderhookNew200x200.jpg?raw=true'
      }
    ],
    title: "Software Engineering Syllabus"
  }
} 



// WEBPACK FOOTER //
// ./src/configs/syllabus-config.js